import React from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@material-ui/core';

import type { FlaggedMessage } from '../useInappropriateUseSection';
import './_inappropriate-use-card.scss';

type InappropriateUseCardProps = {
  message: FlaggedMessage;
};

const MAX_VISIBLE_TAGS = 2;

const CATEGORY_KEY_MAP: Record<string, string> = {
  'self-harm': 'ai_analytics_category_self_harm',
  'self-harm/intent': 'ai_analytics_category_self_harm_intent',
  'self-harm/instructions': 'ai_analytics_category_self_harm_instructions',
  hate: 'ai_analytics_category_hate',
  'hate/threatening': 'ai_analytics_category_hate_threatening',
  harassment: 'ai_analytics_category_harassment',
  'harassment/threatening': 'ai_analytics_category_harassment_threatening',
  violence: 'ai_analytics_category_violence',
  'violence/graphic': 'ai_analytics_category_violence_graphic',
  sexual: 'ai_analytics_category_sexual',
  'sexual/minors': 'ai_analytics_category_sexual_minors',
};

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month}, ${hours}:${minutes}`;
}

function getInitials(name: string, lastName: string): string {
  const firstInitial = name?.charAt(0)?.toUpperCase() || '';
  const lastInitial = lastName?.charAt(0)?.toUpperCase() || '';
  return `${firstInitial}${lastInitial}`;
}

function getCategoryLabel(category: string, t: (key: string) => string): string {
  const translationKey = CATEGORY_KEY_MAP[category];
  return translationKey ? t(`chatbot:${translationKey}`) : category;
}

function InappropriateUseCard({ message }: InappropriateUseCardProps): JSX.Element {
  const { t } = useTranslation(['chatbot']);

  const isPending = message.annotationStatus === 'PENDING';
  const isAttended = message.annotationStatus === 'ATTENDED';
  const isDismissed = message.annotationStatus === 'DISMISSED';
  const statusModifier = message.annotationStatus.toLowerCase();

  const visibleTags = message.categories.slice(0, MAX_VISIBLE_TAGS);
  const hiddenTags = message.categories.slice(MAX_VISIBLE_TAGS);
  const hasOverflow = hiddenTags.length > 0;

  const tooltipContent = message.categories.map((cat) => getCategoryLabel(cat.category, t)).join(', ');

  return (
    <div className={`inappropriate-use-card inappropriate-use-card--${statusModifier}`}>
      <div className="inappropriate-use-card__content">
        <div className="inappropriate-use-card__header">
          <div className="inappropriate-use-card__avatar">
            {getInitials(message.studentName, message.studentLastName)}
          </div>
          <div className="inappropriate-use-card__header-info">
            <span className="inappropriate-use-card__student-name">
              {message.studentName} {message.studentLastName}
            </span>
            <span className="inappropriate-use-card__subtitle">
              {formatDate(message.createdAt)}
              {message.groupName && (
                <>
                  <span className="inappropriate-use-card__subtitle-separator">&bull;</span>
                  {message.groupName}
                </>
              )}
            </span>
          </div>
        </div>

        <div className="inappropriate-use-card__tags">
          {visibleTags.map((cat) => (
            <span key={cat.category} className="inappropriate-use-card__tag">
              {getCategoryLabel(cat.category, t)}
            </span>
          ))}
          {hasOverflow && (
            <Tooltip title={tooltipContent} placement="top" arrow>
              <button type="button" className="inappropriate-use-card__tags-more" aria-label={t('chatbot:ai_analytics_inappropriate_use_view_all', { count: message.categories.length })}>
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="11" stroke="currentColor" strokeWidth="1.5" />
                  <path d="M12 7V17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  <path d="M7 12H17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
            </Tooltip>
          )}
        </div>

        <div className="inappropriate-use-card__bubble-wrapper">
          {isPending && (
            <div className="inappropriate-use-card__alert-dot">
              <svg viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="11" cy="11" r="10" stroke="#E04949" strokeWidth="1.5" />
                <path d="M11 6.5V12.5" stroke="#E04949" strokeWidth="1.5" strokeLinecap="round" />
                <circle cx="11" cy="15.5" r="1" fill="#E04949" />
              </svg>
            </div>
          )}
          <div className={`inappropriate-use-card__bubble inappropriate-use-card__bubble--${statusModifier}`}>
            <p className="inappropriate-use-card__bubble-text">{message.content}</p>
          </div>
        </div>

        <div className="inappropriate-use-card__footer">
          {isPending && (
            <span className="inappropriate-use-card__status-label inappropriate-use-card__status-label--pending">
              {t('chatbot:ai_analytics_inappropriate_use_status_pending')}
            </span>
          )}
          {isAttended && (
            <>
              <span className="inappropriate-use-card__status-label inappropriate-use-card__status-label--muted">
                {t('chatbot:ai_analytics_inappropriate_use_status_attended_by')}
              </span>
              {message.reviewerName && (
                <span className="inappropriate-use-card__reviewer-name">{message.reviewerName}</span>
              )}
            </>
          )}
          {isDismissed && (
            <>
              <span className="inappropriate-use-card__status-label inappropriate-use-card__status-label--muted">
                {t('chatbot:ai_analytics_inappropriate_use_status_dismissed_by')}
              </span>
              {message.reviewerName && (
                <span className="inappropriate-use-card__reviewer-name">{message.reviewerName}</span>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default InappropriateUseCard;
